import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export default function ModernTimeDropdown({ placeholder, value, onChange, options = [], width='w-56', height='w-8', isDisabled=false, backgroundColor='', isMulti=false, showBulkActions=false, id, ariaLabel, fontSize='14px' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stagedValue, setStagedValue] = useState([]);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const effectiveIsMulti = isMulti || Array.isArray(value);
  
  // Support the existing string options as well as { value, label } and
  // [value, label] options used by configuration forms.
  const safeOptions = Array.isArray(options) ? options : [];
  const normalizedOptions = safeOptions.map((option) => {
    if (Array.isArray(option)) {
      return { value: String(option[0] ?? ''), label: String(option[1] ?? option[0] ?? '') };
    }
    if (option && typeof option === 'object') {
      return { value: String(option.value ?? ''), label: String(option.label ?? option.value ?? '') };
    }
    return { value: String(option ?? ''), label: String(option ?? '') };
  });
  const allOptionValues = Array.from(new Set(normalizedOptions.map((option) => option.value)));
  const normalizeToArray = (val) => {
    if (Array.isArray(val)) return val;
    if (val === null || typeof val === 'undefined' || val === '') return [];
    return [String(val)];
  };
  
  // Filter options based on search term
  const filteredOptions = normalizedOptions.filter(option =>
    `${option.label} ${option.value}`.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const showSearch = normalizedOptions.length > 5;
  
  const toggleDropdown = () => {
    const nextIsOpen = !isOpen;
    setIsOpen(nextIsOpen);
    if (nextIsOpen && effectiveIsMulti) {
      setStagedValue(normalizeToArray(value));
    }
    setSearchTerm(''); // Reset search when opening/closing
  };
  
  const handleSelection = (optionValue) => {
    if (!effectiveIsMulti) {
      onChange({ target: { value: optionValue } });
      setIsOpen(false);
      setSearchTerm('');
      triggerRef.current?.focus();
      return;
    }
    const current = Array.isArray(stagedValue) ? stagedValue : [];
    const exists = current.includes(optionValue);
    const updated = exists ? current.filter(v => v !== optionValue) : [...current, optionValue];
    setStagedValue(updated);
  };

  const handleApply = () => {
    onChange({ target: { value: stagedValue } });
    setIsOpen(false);
    setSearchTerm('');
    triggerRef.current?.focus();
  };

  const handleCancel = () => {
    setStagedValue(normalizeToArray(value));
    setIsOpen(false);
    setSearchTerm('');
    triggerRef.current?.focus();
  };

  const handleSelectAll = () => {
    setStagedValue(allOptionValues);
  };

  const handleClearAll = () => {
    setStagedValue([]);
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const getMultiValueDisplay = () => {
    const selected = normalizeToArray(value);
    if (!selected.length) return placeholder || 'Select';
    const labels = selected.map((selectedValue) => (
      normalizedOptions.find((option) => option.value === String(selectedValue))?.label || selectedValue
    ));
    const previewLimit = 2;
    if (labels.length <= previewLimit) return labels.join(', ');
    return `${labels.slice(0, previewLimit).join(', ')} +${labels.length - previewLimit} more`;
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (effectiveIsMulti) {
          setStagedValue(normalizeToArray(value));
        }
        setIsOpen(false);
        setSearchTerm(''); // Reset search when closing
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      handleCancel();
      triggerRef.current?.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        toggleDropdown();
      }
      return;
    }
    if (event.target === searchInputRef.current && ['Home', 'End'].includes(event.key)) return;
    const items = Array.from(dropdownRef.current.querySelectorAll('[role="option"]'));
    if (!items.length) return;
    event.preventDefault();
    const index = items.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
      : event.key === 'ArrowDown' ? (index + 1) % items.length : (index <= 0 ? items.length : index) - 1;
    items[next].focus();
  };

  useEffect(() => {
    if (isOpen) {
      (showSearch ? searchInputRef.current : dropdownRef.current?.querySelector('[role="option"]'))?.focus();
    }
  }, [isOpen, showSearch]);

  return (
    <div className={`relative ${width}`} ref={dropdownRef} onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        id={id}
        aria-label={ariaLabel}
        type="button"
        className={`flex items-center justify-between w-full px-2 py-1.5 text-sm font-medium text-gray-700 ${backgroundColor} border border-gray-200 rounded-md shadow-sm focus:outline-none global-input ${height}`}
        style={{ fontSize }}
        onClick={toggleDropdown} disabled={isDisabled}
      >
        <span
          className={`block min-w-0 truncate text-left ${backgroundColor === 'bg-indigo-600' ? 'text-white' : ''}`}
          title={effectiveIsMulti ? getMultiValueDisplay() : String(value || placeholder || '')}
        >
          {effectiveIsMulti
            ? getMultiValueDisplay()
            : (normalizedOptions.find((option) => option.value === String(value))?.label || value || placeholder)}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${backgroundColor === 'bg-indigo-600' ? 'text-white' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search options..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  style={{ fontSize }}
                />
              </div>
            </div>
          )}
          <ul role="listbox" aria-label={ariaLabel || placeholder || "Options"} aria-multiselectable={effectiveIsMulti || undefined} className="py-1 overflow-auto text-sm max-h-60" style={{ fontSize }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.value}
                  role="option"
                  tabIndex={0}
                  aria-selected={effectiveIsMulti ? stagedValue.includes(option.value) : option.value === String(value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSelection(option.value);
                    }
                  }}
                  className={`px-4 py-2 cursor-pointer hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${
                    effectiveIsMulti
                      ? (Array.isArray(stagedValue) && stagedValue.includes(option.value) ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700')
                      : (option.value === value ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700')
                  }`}
                  onClick={() => handleSelection(option.value)}
                >
                  {effectiveIsMulti ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        tabIndex={-1}
                        readOnly
                        checked={Array.isArray(stagedValue) && stagedValue.includes(option.value)}
                        className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded"
                      />
                      <span>{option.label}</span>
                    </div>
                  ) : (
                    option.label
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500 text-center">
                No options found
              </li>
            )}
          </ul>
          {effectiveIsMulti && (
            <div className="flex items-center justify-between gap-2 px-2 py-2 border-t border-gray-200">
              <div className="flex items-center gap-2">
                {showBulkActions && (
                  <>
                    <button
                      type="button"
                      className="px-2.5 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                      onClick={handleSelectAll}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="px-2.5 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                      onClick={handleClearAll}
                    >
                      Clear all
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700"
                  onClick={handleApply}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
